import { useEffect, useState, ReactNode } from "react";

/** Design canvas size matching the Make a Wish portrait kiosk. */
export const PORTRAIT_WIDTH = 800;
export const PORTRAIT_HEIGHT = 1280;

export const PORTRAIT_STAGE_ID = "portrait-stage";
export const PORTRAIT_VIEWPORT_ID = "portrait-viewport";

/** Mount point for full-screen backgrounds that should not be scaled. */
export const getPortraitViewportContainer = () =>
  document.getElementById(PORTRAIT_VIEWPORT_ID);

/** Mount point for portals (modals) so they scale with the stage. */
export const getPortraitStageContainer = () =>
  document.getElementById(PORTRAIT_STAGE_ID);

type PortraitStageProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Scales a fixed portrait stage to fit the viewport while preserving aspect ratio.
 * Uses min (contain) so top/left UI stays visible when device height changes.
 */
function PortraitStage({ children, className = "" }: PortraitStageProps) {
  const [scale, setScale] = useState(1);
  const [portalReady, setPortalReady] = useState(false);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    const update = () => {
      const sx = window.innerWidth / PORTRAIT_WIDTH;
      const sy = window.innerHeight / PORTRAIT_HEIGHT;
      setScale(Math.min(sx, sy));
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return (
    <div
      id={PORTRAIT_VIEWPORT_ID}
      className={`portraitViewport ${className}`.trim()}
      data-portal-ready={portalReady ? "true" : "false"}
    >
      <div
        id={PORTRAIT_STAGE_ID}
        className="portraitStage"
        style={{
          width: PORTRAIT_WIDTH,
          height: PORTRAIT_HEIGHT,
          transform: `scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default PortraitStage;
